import React, {Component} from 'react';
import {action, observable, computed} from 'mobx';
import {inject, observer} from 'mobx-react';
import moment from "moment";
import PageTitle from "../../../components/common/PageTitle";
import FeatherIcon from 'feather-icons-react';
import {Badge, Button, FormCheckbox, Modal, ModalBody, ModalHeader} from "shards-react";
import {toast} from "react-toastify";
import './shopMileage.scss';

/**
 * 샵 마일리지 (mileageConfig 복구 도구) — SuperAdmin 전용
 *
 * mileageConfig 컬렉션이 삭제된 상황에서:
 *  1) 현재 유료 샵 목록을 조회하고 각 샵의 mileageConfig 존재 여부를 표시
 *  2) 설정이 없는 샵은 userMileageHistory 를 분석해 원래의 적립금 설정값(mileageConfig)을 추론
 *
 * 추론 근거 — userMileageHistory.reason 패턴:
 *  · "상품후기 - ..."   → mileageSettings.member (금액대별 text/image/movie)
 *  · "[이미지]"         → mileageBonusSettings.perImg.mileage_1
 *  · "[장문]"           → mileageBonusSettings.perText.mileage_1
 *  · "[회원 최초]"       → mileageBonusSettings.joinFirst.mileage_1
 *  · "[선착순]"         → mileageBonusSettings.fsb.mileage_1
 *  · "[조기작성]"       → mileageBonusSettings.early.mileage_1
 */
@inject(({teamUser}) => ({isLogin: teamUser.isLogin, me: teamUser.me}))
@inject(({AOA}) => ({
  getShopList: AOA.getShopList,
  getShopMileageStatus: AOA.getShopMileageStatus,
  getUserMileageHistoryByStore: AOA.getUserMileageHistoryByStore,
  getOrphanMileageConfigs: AOA.getOrphanMileageConfigs,
  deleteOrphanMileageConfig: AOA.deleteOrphanMileageConfig,
  deleteOrphanMileageConfigs: AOA.deleteOrphanMileageConfigs
}))
@observer
class ShopMileage extends Component {

  @observable isLogin = false;
  @observable teamUser = {};
  @observable isLoading = true;

  // 가공된 샵 리스트
  @observable shops = [];
  @observable onlyMissing = false;   // 설정 없는 샵만 보기
  @observable keyword = '';

  // 이력 모달
  @observable isHistoryOpen = false;
  @observable historyLoading = false;
  @observable historyShop = null;
  @observable historyData = null;
  @observable historyPage = 1;
  historyLimit = 100;

  // 고아(store_id 없는) mileageConfig 정리
  @observable orphanCount = 0;
  @observable orphanList = [];
  @observable orphanLoading = false;
  @observable isOrphanOpen = false;
  @observable orphanDeleting = false;

  constructor(props) {
    super(props);
    this.init();
  }

  @action init = async () => {
    if (!this.props.isLogin) return;
    this.isLogin = this.props.isLogin;
    this.teamUser = this.props.me;
    await this.loadShops();
    this.loadOrphans();
  };

  // === 고아 mileageConfig (store_id 없음) ===
  @action loadOrphans = async () => {
    this.orphanLoading = true;
    const res = await this.props.getOrphanMileageConfigs();
    this.orphanList = (res && Array.isArray(res.docs)) ? res.docs : [];
    this.orphanCount = (res && typeof res.count === 'number') ? res.count : this.orphanList.length;
    this.orphanLoading = false;
  };

  @action openOrphan = () => { this.isOrphanOpen = true; if (!this.orphanList.length) this.loadOrphans(); };
  @action closeOrphan = () => { this.isOrphanOpen = false; };

  @action deleteOneOrphan = async (doc) => {
    if (this.orphanDeleting) return;
    if (!window.confirm(`이 비정상 설정(_id: ${doc._id})을 삭제합니다.\nstore_id가 없는 문서만 삭제되며 되돌릴 수 없습니다. 계속할까요?`)) return;
    this.orphanDeleting = true;
    const _this = this;
    this.props.deleteOrphanMileageConfig({id: doc._id}, function (res, errors) {
      _this.orphanDeleting = false;
      if (errors && errors.length) { toast.error('삭제 실패: ' + errors[0].message); return; }
      if (res && res.deleted > 0) {
        _this.orphanList = _this.orphanList.filter(o => o._id !== doc._id);
        _this.orphanCount = Math.max(0, _this.orphanCount - res.deleted);
        toast.success('삭제되었습니다.');
      } else {
        toast.warn('삭제 대상이 없습니다. (정상 설정이거나 이미 삭제됨)');
      }
    });
  };

  @action deleteAllOrphans = async () => {
    if (this.orphanDeleting) return;
    if (!window.confirm(`store_id 없는 비정상 mileageConfig ${this.orphanCount}건을 모두 삭제합니다.\nstore_id가 있는 정상 설정은 영향받지 않으며, 삭제는 되돌릴 수 없습니다. 계속할까요?`)) return;
    this.orphanDeleting = true;
    const _this = this;
    this.props.deleteOrphanMileageConfigs(function (res, errors) {
      _this.orphanDeleting = false;
      if (errors && errors.length) { toast.error('삭제 실패: ' + errors[0].message); return; }
      const n = (res && res.deleted) || 0;
      toast.success(`${n}건 삭제되었습니다.`);
      _this.orphanList = [];
      _this.orphanCount = 0;
      _this.isOrphanOpen = false;
    });
  };

  // store 객체가 populate 되었을 수도, 문자열일 수도 있어 방어적으로 추출
  _storeId = (d) => (d && d.store_id && d.store_id._id)
    ? d.store_id._id
    : (typeof (d && d.store_id) === 'string' ? d.store_id : (d && d._id));
  _storeName = (d) => (d && d.store_id && d.store_id.name) ? d.store_id.name : (d && d.name) || '';
  _domain = (d) => (d && d.store_id && d.store_id.domain) ? d.store_id.domain : (d && d.domain) || '';

  @action loadShops = async () => {
    this.isLoading = true;

    // 1) 유료 샵 목록 (샵리스트와 동일한 isPaidUser 필터)
    const listRes = await this.props.getShopList({
      page: 1, limit: 1000,
      searchParams: {displayAt: 'all', isPaidUser: true}
    });
    let docs = (listRes && Array.isArray(listRes.docs)) ? listRes.docs : [];
    // 샘플샵 제외
    docs = docs.filter(d => {
      const s = d && (d.store_id || d);
      return !(s && s.isSample === true);
    });

    // 2) store_id 목록으로 mileageConfig 존재 여부 + 이력 통계 일괄 조회
    const storeIds = Array.from(new Set(docs.map(d => this._storeId(d)).filter(Boolean)));
    let status = {configs: [], historyStats: []};
    if (storeIds.length > 0) {
      const statusRes = await this.props.getShopMileageStatus({store_ids: storeIds});
      if (statusRes) status = statusRes;
    }

    // config 존재 맵: `${store_id}_${shopNo}` → config
    const configMap = {};
    (status.configs || []).forEach(c => {
      configMap[`${c.store_id}_${c.shopNo || 1}`] = c;
    });
    // 이력 통계 맵: store_id → {count, firstAt, lastAt}
    const historyMap = {};
    (status.historyStats || []).forEach(h => {
      historyMap[String(h._id)] = h;
    });

    // 3) 샵 행 가공
    this.shops = docs.map(d => {
      const store_id = this._storeId(d);
      const shopNo = d.platformShopNo || d.shopNo || 1;
      const cfg = configMap[`${store_id}_${shopNo}`];
      const stat = historyMap[String(store_id)] || {};
      return {
        store_id,
        shopNo,
        storeName: this._storeName(d),
        domain: this._domain(d),
        shopName: d.shopName || '',
        hasConfig: !!cfg,
        configUpdatedAt: cfg ? cfg.updatedAt : null,
        historyCount: stat.count || 0,
        firstAt: stat.firstAt || null,
        lastAt: stat.lastAt || null
      };
    });

    this.isLoading = false;
  };

  @computed get filteredShops() {
    let rows = this.shops.slice();
    if (this.onlyMissing) rows = rows.filter(r => !r.hasConfig);
    const kw = (this.keyword || '').trim().toLowerCase();
    if (kw) {
      rows = rows.filter(r =>
        (r.storeName || '').toLowerCase().includes(kw) ||
        (r.domain || '').toLowerCase().includes(kw) ||
        String(r.store_id).toLowerCase().includes(kw)
      );
    }
    // 설정 없음 + 이력 보유(복구 우선순위) 를 위로
    return rows.sort((a, b) => {
      const aw = (!a.hasConfig && a.historyCount > 0) ? 0 : (!a.hasConfig ? 1 : 2);
      const bw = (!b.hasConfig && b.historyCount > 0) ? 0 : (!b.hasConfig ? 1 : 2);
      if (aw !== bw) return aw - bw;
      return (b.historyCount || 0) - (a.historyCount || 0);
    });
  }

  @computed get stats() {
    const total = this.shops.length;
    const missing = this.shops.filter(r => !r.hasConfig).length;
    const missingWithHistory = this.shops.filter(r => !r.hasConfig && r.historyCount > 0).length;
    return {total, has: total - missing, missing, missingWithHistory};
  }

  @action openHistory = async (shop) => {
    this.historyShop = shop;
    this.isHistoryOpen = true;
    this.historyPage = 1;
    await this.loadHistory();
  };

  @action loadHistory = async () => {
    this.historyLoading = true;
    this.historyData = null;
    const res = await this.props.getUserMileageHistoryByStore({
      store_id: this.historyShop.store_id,
      page: this.historyPage,
      limit: this.historyLimit
    });
    this.historyData = res || null;
    this.historyLoading = false;
  };

  @action closeHistory = () => {
    this.isHistoryOpen = false;
    this.historyShop = null;
    this.historyData = null;
  };

  @action changeHistoryPage = async (delta) => {
    const totalPages = this.historyData ? Math.max(1, Math.ceil(this.historyData.totalCount / this.historyLimit)) : 1;
    const next = this.historyPage + delta;
    if (next < 1 || next > totalPages) return;
    this.historyPage = next;
    await this.loadHistory();
  };

  fmt = (n) => (n == null ? '-' : Number(n).toLocaleString());
  fmtDate = (d) => d ? moment(d).format('YYYY-MM-DD') : '-';
  fmtDateTime = (d) => d ? moment(d).format('YYYY-MM-DD HH:mm') : '-';
  fmtPct = (n) => (n == null ? '-' : (Math.round(Number(n) * 100) / 100).toLocaleString() + '%');

  // 리뷰 타입 라벨 (Util.getMileage 분류 기준: movie / image / 그 외=text)
  reviewTypeLabel = (rt) => rt === 'movie' ? '동영상' : rt === 'image' ? '포토' : '텍스트';

  // 고아 mileageConfig 한 줄 요약
  orphanSummary = (o) => {
    const parts = [];
    const ms = (o && o.mileageSettings) || {};
    if (ms.member) parts.push(`기본 텍스트:${ms.member.text || 0}/포토:${ms.member.image || 0}/동영상:${ms.member.movie || 0}`);
    if (ms.isRate) parts.push('비례%');
    if (ms.isAuto) parts.push('자동');
    if (o && o.noticeText) parts.push('안내문');
    return parts.length ? parts.join(' · ') : '(빈 설정)';
  };

  // baseByReviewType([{_id:{reviewType,amount},count}]) → {text:[{amount,count}], image:[...], movie:[...]}
  groupBaseByType = (rows) => {
    const g = {text: [], image: [], movie: []};
    (rows || []).forEach(r => {
      const t = (r._id && r._id.reviewType) || 'text';
      (g[t] = g[t] || []).push({amount: r._id ? r._id.amount : 0, count: r.count});
    });
    Object.keys(g).forEach(k => g[k].sort((a, b) => b.count - a.count));
    return g;
  };

  // 비례율 히스토그램에서 상위 %(높은 적립율) 버킷을 반환 — 실제 설정율은 상단(상한)에 가깝다
  topBuckets = (hist, n) => (hist || []).slice()
    .filter(b => b && b.pct > 0)
    .sort((a, b) => b.pct - a.pct)
    .slice(0, n || 4);

  // 견고한 상한 추정 — 건수 2 이상 버킷 중 최고 적립율(단발성 이상치 제외), 없으면 fallback
  robustEstRate = (hist, fallback) => {
    const sig = (hist || []).filter(b => b && b.pct > 0 && b.count >= 2).sort((a, b) => b.pct - a.pct);
    return sig.length ? sig[0].pct : (fallback != null ? fallback : null);
  };

  // 추론 카드 (reason 패턴 → mileageConfig 필드). statNote: 강조 표시할 조건 추정값(글자수/이미지수)
  renderInferGroup = (title, configPath, rows, hint, statNote) => {
    rows = rows || [];
    const has = rows.length > 0;
    return (
      <div className={`sm-infer__card ${has ? '' : 'is-empty'}`}>
        <div className="sm-infer__card-title">{title}</div>
        <code className="sm-infer__path">{configPath}</code>
        {statNote && <div className="sm-infer__stat">{statNote}</div>}
        {hint && <div className="sm-infer__card-hint">{hint}</div>}
        {has ? (
          <div className="sm-infer__amts">
            {rows.map((r, i) => (
              <div key={i} className={`sm-amt-chip ${i === 0 ? 'is-top' : ''}`} title={`${this.fmt(r.count)}건`}>
                <span className="sm-amt-chip__val">{this.fmt(r._id)}원</span>
                <span className="sm-amt-chip__cnt">{this.fmt(r.count)}건</span>
              </div>
            ))}
          </div>
        ) : <div className="sm-infer__none">해당 적립 이력 없음</div>}
      </div>
    );
  };

  // 기본 적립 추론 — 리뷰 타입별 고정 금액 + 결제금액 비례(%) 두 모드를 나란히 표시
  renderBaseInference = (baseByType, baseRate) => {
    const g = this.groupBaseByType(baseByType);
    const types = ['text', 'image', 'movie'];
    const rateByType = {};
    (baseRate || []).forEach(r => { rateByType[r._id] = r; });
    const hasFixed = (baseByType || []).length > 0;
    return (
      <div className="sm-base">
        <div className="sm-base__head">
          <FeatherIcon icon="layers" size="14"/> 기본 적립 (상품후기) — 리뷰 타입별 추론
        </div>
        <div className="sm-base__cols">
          {/* 고정 금액 모드 */}
          <div className="sm-base__col">
            <div className="sm-base__col-title">① 고정 금액 모드 <span className="sm-muted">(isRate=false)</span></div>
            <code className="sm-infer__path">mileageSettings.member.&#123;text/image/movie&#125;</code>
            <table className="sm-base__table">
              <thead><tr><th>리뷰타입</th><th>적립금 후보 (상위 6, 건수순)</th></tr></thead>
              <tbody>
              {types.map(t => {
                const items = g[t] || [];
                const shown = items.slice(0, 6);
                const rest = items.length - shown.length;
                const scattered = items.length >= 8; // 금액 종류 많음 → 비례 모드 신호
                return (
                  <tr key={t}>
                    <td className="sm-base__type">
                      {this.reviewTypeLabel(t)}
                      {items.length > 0 && (
                        <span className={`sm-base__distinct ${scattered ? 'is-scattered' : ''}`}>{items.length}종</span>
                      )}
                    </td>
                    <td>
                      {items.length ? (
                        <div className="sm-base__chips">
                          {shown.map((x, i) => (
                            <span key={i} className={`sm-amt-chip sm-amt-chip--inline ${i === 0 ? 'is-top' : ''}`}>
                              <span className="sm-amt-chip__val">{this.fmt(x.amount)}원</span>
                              <span className="sm-amt-chip__cnt">{this.fmt(x.count)}건</span>
                            </span>
                          ))}
                          {rest > 0 && <span className="sm-base__more">외 {this.fmt(rest)}종{scattered ? ' · 비례 모드 추정' : ''}</span>}
                        </div>
                      ) : <span className="sm-muted">없음</span>}
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>
          {/* 결제금액 비례(%) 모드 */}
          <div className="sm-base__col">
            <div className="sm-base__col-title">② 결제금액 비례(%) 모드 <span className="sm-muted">(isRate=true)</span></div>
            <code className="sm-infer__path">mileageSettings.member.&#123;...&#125;, isRate=true</code>
            <table className="sm-base__table">
              <thead><tr><th>리뷰타입</th><th>추정 적립율</th><th>상위 분포 (%×건)</th></tr></thead>
              <tbody>
              {types.map(t => {
                const r = rateByType[t];
                const buckets = r ? this.topBuckets(r.histogram, 4) : [];
                const est = r ? this.robustEstRate(r.histogram, r.estRate) : null;
                return (
                  <tr key={t}>
                    <td className="sm-base__type">{this.reviewTypeLabel(t)}</td>
                    <td>
                      {r ? (
                        <span title={'정확 최댓값 ' + this.fmtPct(r.maxPct) + ' · ' + this.fmt(r.count) + '건'}>
                          <b className="sm-base__pct">~{this.fmtPct(est)}</b>
                          <span className="sm-muted"> 상한</span>
                        </span>
                      ) : <span className="sm-muted">없음</span>}
                    </td>
                    <td className="sm-muted">
                      {buckets.length ? buckets.map((b, i) => (
                        <span key={i} className="sm-base__bucket">{this.fmtPct(b.pct)}<span className="sm-base__bucket-cnt">×{this.fmt(b.count)}</span></span>
                      )) : '-'}
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="sm-base__note">
          {hasFixed
            ? <span>타입별 <b>적립금이 일정</b>하면 <b>고정 금액 모드</b>, 타입별 <b>적립율(%)</b>이 일정하면 <b>결제금액 비례 모드</b>입니다. ※ 비례율은 원래 <b>상품 단가</b> 기준이지만 그 값이 저장돼 있지 않아 <b>주문 금액</b>으로 추정합니다 — 분모가 더 크므로 실제 설정율 ≥ 표시값이고, <b>상위 분포(상한)</b>가 실제 적립율에 가장 가깝습니다.</span>
            : <span className="sm-muted">상품후기 기본 적립 이력이 없습니다.</span>}
        </div>
      </div>
    );
  };

  renderOrphanModal() {
    return (
      <Modal open={this.isOrphanOpen} toggle={this.closeOrphan} size="lg" className="sm-modal">
        <ModalHeader toggle={this.closeOrphan}>
          비정상 mileageConfig 정리 · store_id 없음 ({this.fmt(this.orphanCount)}건)
        </ModalHeader>
        <ModalBody>
          <div className="sm-orphan__bar">
            <span className="sm-orphan__desc">
              <FeatherIcon icon="alert-triangle" size="14"/> store_id 값이 없는 비정상 설정 문서입니다. 삭제해도 <b>정상 설정(store_id 보유)에는 영향이 없습니다.</b>
            </span>
            <div className="sm-orphan__actions">
              <Button theme="light" size="sm" onClick={this.loadOrphans} disabled={this.orphanLoading || this.orphanDeleting}>
                <FeatherIcon icon="refresh-cw" size="13"/> 새로고침
              </Button>
              <Button theme="danger" size="sm" onClick={this.deleteAllOrphans} disabled={this.orphanDeleting || this.orphanCount === 0}>
                <FeatherIcon icon="trash-2" size="13"/> 전체 삭제 ({this.fmt(this.orphanCount)})
              </Button>
            </div>
          </div>

          {this.orphanLoading ? (
            <div className="sm-loading">불러오는 중…</div>
          ) : this.orphanList.length === 0 ? (
            <div className="sm-empty" style={{padding: '28px 0'}}>store_id 없는 비정상 설정이 없습니다.</div>
          ) : (
            <div className="sm-table-wrap">
              <table className="sm-table sm-table--sm">
                <thead>
                <tr>
                  <th style={{width: '220px'}}>_id</th>
                  <th style={{width: '60px'}}>shopNo</th>
                  <th style={{width: '150px'}}>생성 / 수정</th>
                  <th>설정 요약</th>
                  <th style={{width: '74px'}}/>
                </tr>
                </thead>
                <tbody>
                {this.orphanList.map((o, i) => (
                  <tr key={o._id || i}>
                    <td className="sm-reason"><code>{o._id}</code></td>
                    <td>{o.shopNo != null ? o.shopNo : '-'}</td>
                    <td className="sm-muted">{this.fmtDate(o.createdAt)}<br/>{this.fmtDate(o.updatedAt)}</td>
                    <td className="sm-muted">{this.orphanSummary(o)}</td>
                    <td>
                      <Button theme="danger" size="sm" outline disabled={this.orphanDeleting} onClick={() => this.deleteOneOrphan(o)}>
                        <FeatherIcon icon="trash-2" size="13"/> 삭제
                      </Button>
                    </td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
      </Modal>
    );
  }

  renderHistoryModal() {
    const shop = this.historyShop;
    const d = this.historyData;
    const inf = (d && d.inference) || {};
    const totalPages = d ? Math.max(1, Math.ceil(d.totalCount / this.historyLimit)) : 1;
    const ptls = d && d.perTextLenStats;
    const pics = d && d.perImgCountStats;
    const perTextNote = ptls
      ? `장문 기준(range_1) 추정: ${this.fmt(ptls.minLen)}자 이하 · 보너스 받은 리뷰 글자수 ${this.fmt(ptls.minLen)}~${this.fmt(ptls.maxLen)}자 (평균 ${this.fmt(Math.round(ptls.avgLen))} · ${this.fmt(ptls.count)}건)`
      : null;
    const perImgNote = pics
      ? `이미지 기준(range_1) 추정: ${this.fmt(pics.minCount)}장 이하 · 보너스 받은 리뷰 이미지수 ${this.fmt(pics.minCount)}~${this.fmt(pics.maxCount)}장 (평균 ${this.fmt(Math.round(pics.avgCount))} · ${this.fmt(pics.count)}건)`
      : null;
    return (
      <Modal open={this.isHistoryOpen} toggle={this.closeHistory} size="lg" className="sm-modal">
        <ModalHeader toggle={this.closeHistory}>
          마일리지 이력 / 추론 · {shop.storeName || '(이름없음)'}
          <span className="sm-muted"> · shopNo {shop.shopNo}</span>
        </ModalHeader>
        <ModalBody>
          {this.historyLoading ? (
            <div className="sm-loading">불러오는 중…</div>
          ) : !d ? (
            <div className="sm-loading">데이터를 불러오지 못했습니다.</div>
          ) : (
            <div>
              {/* === 추론 === */}
              <h4 className="sm-section-title"><FeatherIcon icon="zap" size="15"/> mileageConfig 추론 (reason 패턴 분석)</h4>
              <p className="sm-infer__hint">
                아래 금액이 복구할 설정값 후보입니다. <b>건수가 많은 금액(강조)</b>이 실제 설정값일 가능성이 높습니다.
              </p>
              {/* 기본 적립 — 리뷰 타입별 (고정 금액 / 결제금액 비례 %) */}
              {this.renderBaseInference(d.baseByReviewType, d.baseRateByReviewType)}

              {/* 보너스 적립 */}
              <div className="sm-infer__grid">
                {this.renderInferGroup('보너스 · 포토(이미지)', 'mileageBonusSettings.perImg.mileage_1', inf.bonusPerImg,
                  null, perImgNote)}
                {this.renderInferGroup('보너스 · 장문 텍스트', 'mileageBonusSettings.perText.mileage_1', inf.bonusPerText,
                  null, perTextNote)}
                {this.renderInferGroup('보너스 · 회원 최초', 'mileageBonusSettings.joinFirst.mileage_1', inf.bonusJoinFirst)}
                {this.renderInferGroup('보너스 · 선착순', 'mileageBonusSettings.fsb.mileage_1', inf.bonusFsb)}
                {this.renderInferGroup('보너스 · 조기작성', 'mileageBonusSettings.early.mileage_1', inf.bonusEarly)}
              </div>

              <div className="sm-infer__row">
                <div className="sm-infer__box">
                  <div className="sm-infer__box-title">자동 적립 추론 (mileageSettings.isAuto)</div>
                  {(inf.byAutoPaid || []).length === 0 && <div className="sm-muted">데이터 없음</div>}
                  {(inf.byAutoPaid || []).map((a, i) => (
                    <div key={i} className="sm-kv">
                      <span>{a._id === true ? '자동 지급(true)' : a._id === false ? '수동 지급(false)' : '미지정'}</span>
                      <b>{this.fmt(a.count)}건</b>
                    </div>
                  ))}
                </div>
                <div className="sm-infer__box">
                  <div className="sm-infer__box-title">전체 금액 분포</div>
                  {(inf.byAmount || []).length === 0 && <div className="sm-muted">데이터 없음</div>}
                  {(inf.byAmount || []).map((a, i) => (
                    <div key={i} className="sm-kv">
                      <span>{this.fmt(a._id)}원</span><b>{this.fmt(a.count)}건</b>
                    </div>
                  ))}
                </div>
              </div>

              {/* === 원본 리스트 === */}
              <h4 className="sm-section-title sm-section-title--mt">
                <FeatherIcon icon="list" size="15"/> userMileageHistory 원본 ({this.fmt(d.totalCount)}건)
              </h4>
              <div className="sm-table-wrap">
                <table className="sm-table sm-table--sm">
                  <thead>
                  <tr>
                    <th style={{width: '128px'}}>적립일</th>
                    <th style={{width: '64px'}}>리뷰타입</th>
                    <th style={{width: '80px'}}>금액</th>
                    <th style={{width: '88px'}}>결제금액</th>
                    <th style={{width: '92px'}}>글자수·이미지</th>
                    <th>reason</th>
                    <th style={{width: '62px'}}>지급</th>
                  </tr>
                  </thead>
                  <tbody>
                  {(d.list || []).map((h, i) => (
                    <tr key={i}>
                      <td className="sm-muted">{this.fmtDateTime(h.createdAt)}</td>
                      <td>
                        <span className={`sm-rt sm-rt--${h.reviewType || 'text'}`}>{this.reviewTypeLabel(h.reviewType)}</span>
                        {h.type === 'decrease' && <span className="sm-rt sm-rt--dec">차감</span>}
                      </td>
                      <td className="sm-amt">{this.fmt(h.mileageAmout)}원</td>
                      <td className="sm-muted">{h.orderAmount > 0 ? this.fmt(h.orderAmount) + '원' : '-'}</td>
                      <td className="sm-muted">
                        {this.fmt(h.textLen || 0)}자{h.imageCount > 0 ? ` · ${this.fmt(h.imageCount)}장` : ''}
                      </td>
                      <td className="sm-reason">{h.reason}</td>
                      <td>{h.isAutoPaid ? <Badge theme="info">자동</Badge> : <span className="sm-muted">수동</span>}</td>
                    </tr>
                  ))}
                  {(d.list || []).length === 0 && (
                    <tr><td colSpan={7} className="sm-empty">이력이 없습니다.</td></tr>
                  )}
                  </tbody>
                </table>
              </div>

              <div className="sm-pager">
                <Button theme="light" size="sm" disabled={this.historyPage <= 1} onClick={() => this.changeHistoryPage(-1)}>
                  <FeatherIcon icon="chevron-left" size="14"/> 이전
                </Button>
                <span className="sm-pager__info">{this.historyPage} / {totalPages}</span>
                <Button theme="light" size="sm" disabled={this.historyPage >= totalPages} onClick={() => this.changeHistoryPage(1)}>
                  다음 <FeatherIcon icon="chevron-right" size="14"/>
                </Button>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>
    );
  }

  render() {
    const s = this.stats;
    return (
      <div className="main-content sm-page">
        <PageTitle/>

        {/* 안내 */}
        <div className="sm-intro">
          <div className="sm-intro__icon"><FeatherIcon icon="database" size="20"/></div>
          <div>
            <h3 className="sm-intro__title">샵 마일리지 — mileageConfig 복구 도구</h3>
            <p className="sm-intro__desc">
              현재 유료 샵의 <b>mileageConfig</b> 데이터 존재 여부를 확인합니다.
              설정이 <b>없는</b> 샵은 <b>userMileageHistory</b> 적립 이력을 분석하여 원래의 적립금 설정값을 추론할 수 있습니다.
            </p>
          </div>
        </div>

        {/* 요약 */}
        <div className="sm-summary">
          <div className="sm-stat">
            <span className="sm-stat__num">{s.total}</span><span className="sm-stat__label">전체 유료 샵</span>
          </div>
          <div className="sm-stat sm-stat--ok">
            <span className="sm-stat__num">{s.has}</span><span className="sm-stat__label">설정 있음</span>
          </div>
          <div className="sm-stat sm-stat--warn">
            <span className="sm-stat__num">{s.missing}</span><span className="sm-stat__label">설정 없음</span>
          </div>
          <div className="sm-stat sm-stat--info">
            <span className="sm-stat__num">{s.missingWithHistory}</span><span className="sm-stat__label">복구 가능 (이력 보유)</span>
          </div>
        </div>

        {/* store_id 없는 비정상 mileageConfig 경고 배너 */}
        {this.orphanCount > 0 && (
          <div className="sm-orphan-banner">
            <span className="sm-orphan-banner__msg">
              <FeatherIcon icon="alert-triangle" size="16"/>
              <span><b>store_id 없는 비정상 mileageConfig {this.fmt(this.orphanCount)}건</b> 발견 — 버그로 잘못 생성된 문서입니다.</span>
            </span>
            <Button theme="warning" size="sm" onClick={this.openOrphan}>
              <FeatherIcon icon="tool" size="13"/> 조회 / 정리
            </Button>
          </div>
        )}

        {/* 컨트롤 */}
        <div className="sm-controls">
          <div className="sm-search">
            <FeatherIcon icon="search" size="15"/>
            <input placeholder="스토어명 / 도메인 / store_id 검색"
                   value={this.keyword}
                   onChange={(e) => this.keyword = e.target.value}/>
          </div>
          <FormCheckbox toggle small checked={this.onlyMissing} onChange={() => this.onlyMissing = !this.onlyMissing}>
            설정 없는 샵만
          </FormCheckbox>
          <Button theme="light" size="sm" onClick={this.loadShops} disabled={this.isLoading}>
            <FeatherIcon icon="refresh-cw" size="13"/> 새로고침
          </Button>
        </div>

        {/* 테이블 */}
        {this.isLoading ? (
          <div className="sm-loading"><FeatherIcon icon="loader" size="16"/> 유료 샵 / 마일리지 설정을 불러오는 중…</div>
        ) : (
          <div className="sm-table-wrap">
            <table className="sm-table">
              <thead>
              <tr>
                <th>스토어</th>
                <th style={{width: '80px'}}>shopNo</th>
                <th style={{width: '120px'}}>mileageConfig</th>
                <th style={{width: '110px'}}>마일리지 이력</th>
                <th style={{width: '200px'}}>적립 기간</th>
                <th style={{width: '130px'}}/>
              </tr>
              </thead>
              <tbody>
              {this.filteredShops.map((r, i) => (
                <tr key={`${r.store_id}_${r.shopNo}_${i}`} className={!r.hasConfig ? 'is-missing' : ''}>
                  <td>
                    <div className="sm-store">
                      <span className="sm-store__name">{r.storeName || '(이름없음)'}</span>
                      <span className="sm-store__meta">{r.domain || '-'} · {r.store_id}</span>
                    </div>
                  </td>
                  <td>{r.shopNo}</td>
                  <td>
                    {r.hasConfig
                      ? <Badge theme="success">있음</Badge>
                      : <Badge theme="danger">없음</Badge>}
                  </td>
                  <td>
                    {r.historyCount > 0
                      ? <b>{this.fmt(r.historyCount)}건</b>
                      : <span className="sm-muted">0건</span>}
                  </td>
                  <td className="sm-muted">
                    {r.historyCount > 0 ? `${this.fmtDate(r.firstAt)} ~ ${this.fmtDate(r.lastAt)}` : '-'}
                  </td>
                  <td>
                    <Button theme={r.hasConfig ? 'light' : 'primary'} size="sm"
                            disabled={r.historyCount === 0}
                            onClick={() => this.openHistory(r)}>
                      <FeatherIcon icon="search" size="13"/> 이력/추론
                    </Button>
                  </td>
                </tr>
              ))}
              {this.filteredShops.length === 0 && (
                <tr><td colSpan={6} className="sm-empty">표시할 샵이 없습니다.</td></tr>
              )}
              </tbody>
            </table>
          </div>
        )}

        {this.isHistoryOpen && this.renderHistoryModal()}
        {this.isOrphanOpen && this.renderOrphanModal()}
      </div>
    );
  }
}

export default ShopMileage;
